import http from './http'

// Convert store tempo string ("80–100", "140+") to API minBpm / maxBpm params.
function tempoToParams (tempo) {
  if (!tempo || tempo === 'All') return {}
  if (tempo.indexOf('+') !== -1) {
    return { minBpm: parseInt(tempo) }
  }
  var parts = tempo.replace(/[–—-]/, '-').split('-')
  var result = { minBpm: parseInt(parts[0]) }
  if (parts[1]) result.maxBpm = parseInt(parts[1])
  return result
}

// Map a Spring Page response to the shape the Vuex store expects.
function mapPage (res) {
  var page = res.data
  return {
    items: page.content || [],
    total: page.totalElements || 0,
    hasMore: !page.last
  }
}

/**
 * Fetch a paginated list of published beats.
 * genre, tempo (store format), mood, producerId and sort are optional.
 * page is 1-indexed (converted to 0-indexed for Spring internally).
 */
export function listBeats (params) {
  var query = {
    page: params && params.page > 1 ? params.page - 1 : 0,
    size: (params && params.size) || 20
  }

  if (params && params.genre && params.genre !== 'All') {
    query.genre = params.genre
  }

  var bpmParams = tempoToParams(params && params.tempo)
  if (bpmParams.minBpm) query.minBpm = bpmParams.minBpm
  if (bpmParams.maxBpm) query.maxBpm = bpmParams.maxBpm

  if (params && params.mood) query.mood = params.mood
  if (params && params.producerId) query.producerId = params.producerId
  if (params && params.sort) query.sort = params.sort

  return http.get('/beats', { params: query }).then(mapPage)
}

/**
 * Get full beat detail (licences, producer, presigned preview URL, play count).
 * Increments the play count.
 * @param {string} beatId
 */
export function getBeat (beatId) {
  return http.get('/beats/' + beatId).then(function (res) {
    return res.data
  })
}

/**
 * Get a 30-min presigned PUT URL for uploading a beat or preview file to MinIO.
 * Requires ENGINEER or ADMIN role.
 * @param {string} objectKey  e.g. "beats/night-drive.mp3"
 * @returns {Promise<{ url: string }>}
 */
export function getBeatUploadUrl (objectKey) {
  return http.get('/beats/upload-url', { params: { objectKey: objectKey } }).then(function (res) {
    return res.data
  })
}

/**
 * Create a beat in DRAFT state. The previewFileKey must already be in MinIO.
 * Requires ENGINEER or ADMIN role.
 * @param {{ title, description, bpm, keySignature, genreTags, moodTags, durationSeconds, previewFileKey }} payload
 */
export function createBeat (payload) {
  return http.post('/beats', payload).then(function (res) {
    return res.data
  })
}

/**
 * Add a licence tier to a beat. The fileKey must already be in MinIO.
 * licenceType: FREE | BASIC | PREMIUM | EXCLUSIVE
 * @param {string} beatId
 * @param {{ licenceType, priceKes, fileKey, fileFormat, usageTerms }} payload
 */
export function addLicence (beatId, payload) {
  return http.post('/beats/' + beatId + '/licences', payload).then(function (res) {
    return res.data
  })
}

/**
 * Publish a beat (makes it visible in public listings).
 * Requires at least one licence to be attached.
 * @param {string} beatId
 */
export function publishBeat (beatId) {
  return http.post('/beats/' + beatId + '/publish').then(function (res) {
    return res.data
  })
}

/**
 * Remove a beat from public listings without deleting it.
 * @param {string} beatId
 */
export function unpublishBeat (beatId) {
  return http.post('/beats/' + beatId + '/unpublish').then(function (res) {
    return res.data
  })
}

/**
 * Toggle the authenticated user's favourite status for a beat.
 * @param {string} beatId
 * @returns {Promise<{ favourited: boolean }>}
 */
export function toggleFavourite (beatId) {
  return http.post('/beats/' + beatId + '/favourite').then(function (res) {
    return res.data
  })
}

/**
 * Get the authenticated engineer's own beats — all statuses including drafts, newest first.
 * Requires auth (ENGINEER or ADMIN role).
 * @param {{ page?: number, size?: number }} params
 */
export function getMyBeats (params) {
  var query = {
    page: params && params.page > 1 ? params.page - 1 : 0,
    size: (params && params.size) || 20
  }
  return http.get('/beats/my', { params: query }).then(mapPage)
}

/**
 * Get the authenticated user's favourited beats (paginated).
 * @param {{ page?: number, size?: number }} params
 */
export function getFavourites (params) {
  var query = {
    page: params && params.page > 1 ? params.page - 1 : 0,
    size: (params && params.size) || 20
  }
  return http.get('/beats/favourites', { params: query }).then(mapPage)
}
